import React from "react";
import { Stack, FormControlLabel, Checkbox, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createVoteHead,
  updateVoteHead,
} from "../redux/actions/voteHeadActions";
import { voteHeadActions } from "../redux/slices/voteHead";
import { cleanFormData } from "../helpers/formHelpers";

import { VoteHeadResponseDto } from "../shared/dtos/voteHead/VoteHeadResponseDto";
import { VoteHeadRequestDto } from "../shared/dtos/voteHead/VoteHeadRequestDto";

const voteHeadSchema = Yup.object().shape({
  name: Yup.string().required("Vote head name is required"),
  description: Yup.string().nullable(),
  priority: Yup.number()
    .required("Priority is required")
    .positive("Priority must be a positive number")
    .integer("Priority must be an integer"),
  recurring: Yup.boolean().required(),
  isOptional: Yup.boolean().required(),
  amount: Yup.number().when("isOptional", {
    is: true,
    then: (schema) =>
      schema
        .required("Amount is required for optional fees")
        .min(0, "Amount cannot be negative"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
});

interface CreateOrUpdateVoteHeadFormProps {
  voteHead?: VoteHeadResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateVoteHeadForm: React.FC<CreateOrUpdateVoteHeadFormProps> = ({
  voteHead = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: VoteHeadRequestDto = {
    name: voteHead?.name || "",
    description: voteHead?.description || "",
    priority: voteHead?.priority || 1,
    recurring: voteHead ? voteHead.recurring : true,
    isOptional: voteHead ? voteHead.isOptional : false,
    amount: voteHead?.amount || 0,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(voteHeadActions.setErrorUpdate(null))
            : () => dispatch(voteHeadActions.setErrorCreate(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={voteHeadSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as VoteHeadRequestDto;

          const cleanedValues: VoteHeadRequestDto = {
            name: rawCleaned.name,
            description: rawCleaned.description || null,
            priority: Number(rawCleaned.priority),
            recurring: Boolean(values.recurring),
            isOptional: Boolean(values.isOptional),
            amount:
              values.isOptional && values.amount !== undefined
                ? Number(values.amount)
                : null,
          };

          if (isEditing && voteHead) {
            dispatch(
              updateVoteHead({ id: voteHead.id, values: cleanedValues }),
            );
          } else {
            dispatch(createVoteHead(cleanedValues));
          }
        }}
        enableReinitialize
      >
        {({
          dirty,
          isValid,
          values,
          handleChange,
        }: FormikProps<VoteHeadRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField name="name" label="Vote Head Name" />
                <FormTextField
                  name="priority"
                  label="Priority"
                  type="number"
                  slotProps={{ htmlInput: { min: 1 } }}
                />
              </Box>

              <FormTextField
                name="description"
                label="Description"
                multiline
                rows={3}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isOptional}
                    onChange={handleChange}
                    name="isOptional"
                  />
                }
                label="Is this an optional fee (e.g., Boarding, Lunch)?"
              />

              {values.isOptional && (
                <FormTextField
                  name="amount"
                  label="Standard Amount (KES)"
                  type="number"
                  slotProps={{ htmlInput: { min: 0 } }}
                  helperText="This is the fixed price charged if this fee is allocated to a student."
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.recurring}
                    onChange={handleChange}
                    name="recurring"
                  />
                }
                label="Is this a recurring fee?"
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Vote Head"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateVoteHeadForm;
